import { Injectable } from "@angular/core";
import * as moment from "moment";
import { chkLength as chkLengthUtil } from "../utils/format-util";

/**
 * Context contract for the calendar components that share setCalendarTime logic.
 * The implementing component (HomeCalenderComponent / TechnicianScheduleComponent)
 * exposes the same mutable fields and helper the original inline method relied on,
 * so the extracted logic behaves byte-for-byte identically.
 */
export interface CalendarTimeContext {
  today: any;
  calender: any;
  mintime: string;
  maxTime: string;
  calendarResourceList: any;
  convertTime12to24: (time12h: string) => string;
}

@Injectable({
  providedIn: "root",
})
export class ScheduleSharedService {
  /**
   * Behaviour-preserving extraction of the previously duplicated
   * setCalendarTime() bodies from HomeCalenderComponent and
   * TechnicianScheduleComponent. `ctx` is the calling component (it satisfies
   * CalendarTimeContext), and `days` is the component's own days array which is
   * mutated in place exactly as before.
   */
  setCalendarTime(ctx: CalendarTimeContext, days: any[], work): void {
    const getDate = moment(ctx.today).format("dddd");
    if (work && work.tcFromTime && work.tcToTime) {
      const dayList: any = [];
      days.forEach((day, dayIndex) => {
        day.work = false;
        if (work.tcWork === "work") {
          day.work = true;
          day.fromHour = work.tcFromTime;
          day.toHour = work.tcToTime;
          if (day.value === getDate) {
            if (ctx.calender) {
              ctx.calender.setOption(
                "minTime",
                moment(work.tcFromTime, ["h:mm A"]).format("HH:mm")
              );
              ctx.calender.setOption(
                "maxTime",
                moment(work.tcToTime, ["h:mm A"]).format("HH:mm")
              );
            } else {
              ctx.mintime = moment(work.tcFromTime, ["h:mm A"]).format(
                "HH:mm"
              );
              ctx.maxTime = moment(work.tcToTime, ["h:mm A"]).format("HH:mm");
            }
          }
        }

        if (work.tcLunch === "lunch") {
          dayList.push({
            dayId: day.id,
            id: work.id,
            fromHour: ctx.convertTime12to24(work.tcLunchFromTime),
            toHour: ctx.convertTime12to24(work.tcLunchToTime),
          });
        }
        if (dayList.length > 0) {
          const group: any[] = dayList.reduce((r, a) => {
            r[a.id] = [...(r[a.id] || []), a];
            return r;
          }, {});
          let resourcesList: any = [];
          if (ctx.calender) {
            if (ctx.calender.getResources().length > 0) {
              ctx.calendarResourceList = resourcesList = ctx.calender
                .getResources()
                .map((r) => {
                  return r.id;
                });
            }
          }

          const lunchList: any = [];
          Object.keys(group).forEach((item) => {
            if (group[item].length > 0) {
              const event: any = {};
              event.startTime = group[item][0].fromHour;
              event.endTime = group[item][0].toHour;
              event.eventStartTime = moment(group[item][0].fromHour, [
                "HH:mm",
              ]).format("hh:mm A");
              event.eventEndTime = moment(group[item][0].toHour, [
                "HH:mm",
              ]).format("hh:mm A");
              event.title = "Lunch";
              event.textColor = "black";
              event.color = "#E1E1E1";
              event.daysOfWeek = [];
              group[item].forEach((child) => {
                event.daysOfWeek.push(child.dayId);
              });
              lunchList.push(event);
              if (ctx.calender) {
                event.resourceIds = resourcesList;
              }
            }
          });
          if (lunchList.length > 0) {
            if (dayIndex === 6) {
              if (ctx.calender) {
                lunchList.forEach((lunch) => {
                  ctx.calender.addEvent(lunch);
                });
              }
            }
          }
        }
      });
    } else {
      const dayIDs: any = [];
      days.forEach((day) => {
        if (day.work === false) {
          dayIDs.push(day.id);
        }
      });
      const closeEvent: any = {};
      closeEvent.title = work.holidayType;
      closeEvent.startTime = "00:00";
      closeEvent.endTime = "23:59";
      if (dayIDs.length > 0) {
        closeEvent.daysOfWeek = dayIDs.sort();
      }
      closeEvent.textColor = "black";
      closeEvent.color = "#E1E1E1";
      let resourcesList: any = [];
      if (ctx.calender) {
        if (ctx.calender.getResources().length > 0) {
          closeEvent.resourceIds = resourcesList = ctx.calender
            .getResources()
            .map((r) => {
              return r.id;
            });
        }
      }
      ctx.calender.addEvent(closeEvent);
    }
  }

  /**
   * Behaviour-preserving extraction of the byte-identical (ignoring whitespace)
   * `getMonthNumberBymonthName(valuearray)` bodies from HomeCalenderComponent and
   * TechnicianScheduleComponent. Uses the shared `chkLength` util that both
   * components already delegated to.
   */
  getMonthNumberBymonthName(valuearray): string {
    let month = 0;
    const value = valuearray.toString().split(" ");
    const day = value[1];
    if (value.length > 1) {
      if (value[0] === "January") {
        month = 1;
      } else if (value[0] === "February") {
        month = 2;
      } else if (value[0] === "March") {
        month = 3;
      } else if (value[0] === "April") {
        month = 4;
      } else if (value[0] === "May") {
        month = 5;
      } else if (value[0] === "June") {
        month = 6;
      } else if (value[0] === "July") {
        month = 7;
      } else if (value[0] === "August") {
        month = 8;
      } else if (value[0] === "September") {
        month = 9;
      } else if (value[0] === "October") {
        month = 10;
      } else if (value[0] === "November") {
        month = 11;
      } else if (value[0] === "December") {
        month = 12;
      }
    }
    return (
      value[2] +
      "-" +
      chkLengthUtil(month) +
      "-" +
      chkLengthUtil(day.replace(/,/g, ""))
    );
  }

  /**
   * Behaviour-preserving extraction of the byte-identical `formatHomeDate(date)`
   * bodies shared by both calendar components.
   */
  formatHomeDate(date): string {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) {
      month = "0" + month;
    }
    if (day.length < 2) {
      day = "0" + day;
    }

    return [year, month, day].join("-");
  }

  /**
   * Behaviour-preserving extraction of the byte-identical `getInsurance(type)`
   * bodies shared by both calendar components.
   */
  getInsurance(type): string {
    let viewType = "";
    if (type === 2) {
      viewType = "HMO";
    } else if (type === 3) {
      viewType = "HMO";
    }
    return viewType;
  }

  /**
   * Behaviour-preserving extraction of the byte-identical `convertTime12to24`
   * bodies shared by both calendar components.
   */
  convertTime12to24(time12h): string {
    const [time, modifier] = time12h.split(" ");

    let [hours, minutes] = time.split(":");
    const min = minutes;
    minutes = min;
    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    }

    return `${hours}:${minutes}`;
  }

  /**
   * Behaviour-preserving extraction of the byte-identical
   * `window["angularComponentRef"]` registration performed inside both calendar
   * component constructors. `ctx` is the calling component.
   */
  registerAngularComponentRef(ctx: any): void {
    window["angularComponentRef"] = {
      zone: ctx.zone,
      componentFn: (value) => ctx.navigate(value),
      component: ctx,
    };
  }

  /**
   * Behaviour-preserving extraction of the byte-identical holiday-check
   * subscribe callback shared by the `viewTestCenter()` methods of both calendar
   * components.
   */
  applyHolidayCheckResult(ctx: any, res): void {
    if (res && res["status"] && res["status"]["status"] === 200) {
      if (res["entity"]) {
        if (res["entity"]["countryCode"]) {
          ctx.tcCountryCode = res["entity"]["countryCode"]
            ? res["entity"]["countryCode"]
            : "US";
        }
        ctx.setCalendarTime(res["entity"]);
      }
    } else {
      ctx.setCalendarTime(res["entity"]);
    }
  }
}
